import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { leadApi } from "../../pages/services/leadService";
import { CampaignFilter, StatusPill, errorText, fmtDateTime, fmtDue, useCampaignOptions } from "./leadUtils";
import "../../styles/Leads.css";

// The lead dashboard: the numbers, course by course, and the two follow-up
// lists a sales person works from. Every number opens the lead list filtered
// to exactly those leads.
const STATS = [
  { key: "total", label: "Total leads", to: {} },
  { key: "new", label: "New leads", note: "Not called yet", to: { statuses: "created" }, tone: "warn" },
  { key: "active", label: "Active leads", note: "Created, explained, demo, interested", to: { groups: "active" } },
  { key: "passive", label: "Passive leads", note: "Not reachable / no response / not spoken", to: { groups: "passive" }, tone: "warn" },
  { key: "later", label: "Interested for later", to: { groups: "later" } },
  { key: "dueToday", label: "Follow-ups due today", to: { followUp: "today" }, tone: "warn" },
  { key: "overdue", label: "Overdue follow-ups", to: { followUp: "overdue" }, tone: "bad" },
  { key: "demoScheduled", label: "Demo scheduled", note: "Upcoming sessions", to: { statuses: "ready_for_demo" } },
  { key: "converted", label: "Converted", note: "Registered", to: { statuses: "registered" }, tone: "good" },
  { key: "dead", label: "Dead leads", note: "Not interested / not qualified", to: { groups: "dead" } },
];

const SAVED = "leadDashboardCampaigns";

const FollowUpList = ({ title, rows, empty, tone }) => {
  const navigate = useNavigate();
  return (
    <div className="ld-card">
      <h3 className="ld-card-title">{title} <span className="ld-card-note">{rows.length ? `${rows.length}${rows.length === 25 ? "+" : ""}` : ""}</span></h3>
      {rows.length ? (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Lead</th><th>Status</th><th>Follow-up</th></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l._id} onClick={() => navigate(`/leads/${l._id}`)}>
                  <td><div className="ld-name">{l.fullName}</div><div className="ld-sub">{l.mobile} · {l.courseTitle}</div></td>
                  <td><StatusPill status={l.status} label={l.statusLabel} group={l.statusGroup} /></td>
                  <td>
                    <div className={`ld-due ${tone}`}>{fmtDue(l.followUpAt)}</div>
                    <div className="ld-sub">{fmtDateTime(l.followUpAt)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="ld-empty">{empty}</div>}
    </div>
  );
};

// Meta Lead Ads: whether leads are arriving. Hidden until Meta is set up on
// the server. A token error is shown in red — without it leads would quietly
// stop coming in.
// The connection check's findings (GET /leads/meta-sync/check): the likely
// cause first, then the facts it is based on. The token itself never comes back.
const MetaCheck = ({ report, onClose }) => {
  const t = report.token;
  const Row = ({ ok, children }) => <li style={{ color: ok ? "#15803d" : "#b91c1c", marginBottom: 3 }}>{ok ? "✓" : "✗"} <span style={{ color: "var(--ld-ink-2)" }}>{children}</span></li>;
  return (
    <div className="ld-card" style={{ marginTop: -6 }}>
      <div className="ld-section-head">
        <h4>Meta connection check</h4>
        <button type="button" className="ld-btn ld-btn-sm" onClick={onClose}>Close</button>
      </div>
      <div className={`ld-alert ${report.causes.length === 1 && report.causes[0].startsWith("Everything") ? "ld-alert-ok" : "ld-alert-error"}`}>
        {report.causes.length === 1 ? report.causes[0] : <ol style={{ margin: 0, paddingLeft: 18 }}>{report.causes.map((c) => <li key={c} style={{ marginBottom: 4 }}>{c}</li>)}</ol>}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
        {t && <Row ok={t.ok}>Access token: {t.ok ? <>{t.type ? `${t.type.replace("_", " ").toLowerCase()} token` : "token"} for <b>{t.owner?.name}</b> ({t.owner?.id}){t.app ? ` · app ${t.app}` : ""}{t.expiresAt ? ` · expires ${t.expiresAt === "never" ? "never" : fmtDateTime(t.expiresAt)}` : ""}</> : t.error}</Row>}
        {t?.ok && t.scopes.length > 0 && <Row ok={!t.missingScopes.length}>Permissions: {t.scopes.join(", ")}{t.missingScopes.length ? ` — missing ${t.missingScopes.join(", ")}` : ""}</Row>}
        {report.page && <Row ok={!report.page.error}>Page {report.page.id}: {report.page.error || <b>{report.page.name}</b>}</Row>}
        {report.pageForms && <Row ok={report.pageForms.ok}>The Page's lead forms: {report.pageForms.ok ? (report.pageForms.list.length ? report.pageForms.list.map((f) => `${f.name} (${f.id}, ${String(f.status || "").toLowerCase()})`).join(" · ") : "none") : report.pageForms.error}</Row>}
        {report.forms.map((f) => (
          <Row key={f.id} ok={f.ok}>META_FORM_IDS {f.id}: {f.ok ? <>form <b>{f.name}</b>{f.page ? ` on ${f.page.name}` : ""} — leads readable</> : f.formError || `form "${f.name}" found, but its leads are not readable: ${f.leadsError}`}</Row>
        ))}
      </ul>
    </div>
  );
};

const MetaSyncCard = ({ onSynced }) => {
  const [sync, setSync] = useState(null);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const load = useCallback(() => { leadApi.metaSync().then(setSync).catch(() => setSync(null)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!sync) return null;
  const c = sync.configured;
  const setUp = c.pageToken && c.appSecret && c.verifyToken && c.course;
  if (!setUp && !sync.saved) return null;

  const run = async () => {
    setRunning(true);
    setMessage("");
    try {
      const r = await leadApi.runMetaSync({ sinceDays: 7 });
      setMessage(`Checked ${r.checked} leads from the last 7 days · ${r.added} new · ${r.merged} merged${r.failed ? ` · ${r.failed} failed` : ""}${r.complete ? "" : " · stopped early, the rest follows in the hourly sync"}`);
      onSynced();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setRunning(false);
      load();
    }
  };

  const check = async () => {
    setChecking(true);
    setMessage("");
    try { setReport(await leadApi.checkMetaConnection()); } catch (e) { setMessage(errorText(e)); } finally { setChecking(false); }
  };

  const missing = [!c.verifyToken && "META_VERIFY_TOKEN", !c.appSecret && "META_APP_SECRET", !c.pageToken && "META_PAGE_ACCESS_TOKEN", !c.course && "META_COURSE_ID", !c.forms && "META_FORM_IDS"].filter(Boolean);
  const err = sync.state?.lastError;
  return (
    <>
    <div className={`ld-card ld-meta-sync${err ? " is-error" : ""}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ld-card-title" style={{ margin: 0 }}>Meta Lead Ads</div>
        <div className="ld-sub">
          {sync.saved.toLocaleString("en-IN")} lead{sync.saved === 1 ? "" : "s"} received
          {sync.state?.lastSuccessAt ? ` · last synced ${fmtDateTime(sync.state.lastSuccessAt)}` : " · not synced yet"}
          {sync.failed ? ` · ${sync.failed} waiting to retry` : ""}
        </div>
        {err && <div className="ld-field-error" style={{ marginTop: 4 }}>Leads are not syncing: {err.message} ({fmtDateTime(err.at)}). {err.code === "190" ? "The Page access token has expired or was revoked — replace META_PAGE_ACCESS_TOKEN." : "Check Meta connection says why."}</div>}
        {sync.recentFailures?.[0] && <div className="ld-field-error" style={{ marginTop: 4 }}>Latest lead not saved ({fmtDateTime(sync.recentFailures[0].updatedAt)}): {sync.recentFailures[0].error}</div>}
        {missing.length > 0 && <div className="ld-field-error" style={{ marginTop: 4 }}>Not set on the server: {missing.join(", ")}</div>}
        {message && <div className="ld-sub" style={{ marginTop: 4 }}>{message}</div>}
      </div>
      <span style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button type="button" className="ld-btn ld-btn-sm" onClick={check} disabled={checking}>{checking ? "Checking…" : "Check Meta connection"}</button>
        <button type="button" className="ld-btn ld-btn-sm" onClick={run} disabled={running || !setUp || !c.forms}>{running ? "Syncing…" : "Sync now"}</button>
      </span>
    </div>
    {report && <MetaCheck report={report} onClose={() => setReport(null)} />}
    </>
  );
};

const LeadDashboard = () => {
  const navigate = useNavigate();
  const campaignOptions = useCampaignOptions();
  const [campaigns, setCampaigns] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SAVED) || "[]"); } catch { return []; }
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    leadApi.dashboard({ campaignIds: campaigns.map((c) => c.value) }).then(setData).catch((e) => setError(errorText(e)));
  }, [campaigns]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { try { localStorage.setItem(SAVED, JSON.stringify(campaigns)); } catch { /* private mode */ } }, [campaigns]);

  const open = (to) => {
    const p = new URLSearchParams({ ...to, ...(campaigns.length ? { campaignIds: campaigns.map((c) => c.value).join(",") } : {}) });
    navigate(`/leads/list?${p}`);
  };

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <h1 className="ld-title">Lead Dashboard</h1>
          <p className="ld-subtitle">Enquiries from the website and campaigns, and what is due today.</p>
        </div>
        <div className="ld-head-actions">
          <Link className="ld-btn" to="/leads/setup">Lead settings</Link>
          <Link className="ld-btn" to="/leads/notifications">Notification log</Link>
          <Link className="ld-btn" to="/leads/registrations">Registrations</Link>
          <Link className="ld-btn ld-btn-primary" to="/leads/list">All leads</Link>
        </div>
      </div>

      <div className="ld-filters">
        <CampaignFilter value={campaigns} onChange={setCampaigns} options={campaignOptions} />
      </div>

      {error && <div className="ld-alert ld-alert-error">{error}</div>}

      <MetaSyncCard onSynced={load} />

      <div className="ld-stats">
        {STATS.map((s) => (
          <button key={s.key} type="button" className={`ld-stat${s.tone ? ` is-${s.tone}` : ""}`} onClick={() => open(s.to)}>
            <div className="ld-stat-label">{s.label}</div>
            <div className="ld-stat-value">{data ? data.counts[s.key].toLocaleString("en-IN") : "–"}</div>
            {s.note && <div className="ld-stat-note">{s.note}</div>}
          </button>
        ))}
      </div>

      <div className="ld-two">
        <FollowUpList title="Overdue follow-ups" rows={data?.overdue || []} empty="Nothing overdue." tone="is-overdue" />
        <FollowUpList title="Follow-ups due today" rows={data?.dueToday || []} empty="No more follow-ups today." tone="is-today" />
      </div>

      <div className="ld-card" style={{ marginTop: 16 }}>
        <h3 className="ld-card-title">Leads by course</h3>
        {data?.byCourse?.length ? (
          <div className="ld-table-wrap">
            <table className="ld-table">
              <thead><tr><th>Course</th><th>Leads</th><th>Registered</th><th>Conversion</th></tr></thead>
              <tbody>
                {data.byCourse.map((c) => (
                  <tr key={c._id} onClick={() => open({ courseIds: c._id })}>
                    <td className="ld-name">{c.courseTitle}</td>
                    <td>{c.total}</td>
                    <td>{c.converted}</td>
                    <td>{c.total ? `${Math.round((c.converted / c.total) * 100)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="ld-empty">{data ? "No leads yet." : "Loading…"}</div>}
      </div>
    </div>
  );
};

export default LeadDashboard;
